// src/hooks/useDocumentTypeSections.ts

import { useState, useCallback } from "react";
import {
  DocumentTypeSection,
  SectionCreateData,
  SectionUpdateData,
} from "../types/documentTypes";
import { useAuthFetch } from "../../../utils/useAuthFetch";

const useCreateDocumentTypeSection = (workspace_id: string) => {
  const [loading, setLoading] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);
  const authFetch = useAuthFetch();

  const createSection = async (
    documentTypeId: number,
    sectionData: SectionCreateData,
  ): Promise<DocumentTypeSection> => {
    try {
      setLoading(true);
      const response = await authFetch(
        `/api/workspace/meridian/admin/document_types/${documentTypeId}/sections?workspace_id=${workspace_id}`,
        "create section",
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
          },
          body: JSON.stringify(sectionData),
        },
      );
      return response.section || response;
    } catch (err: any) {
      setError(err.message || "Error creating section.");
      throw err;
    } finally {
      setLoading(false);
    }
  };

  return { createSection, loading, error };
};

const useUpdateDocumentTypeSection = (workspace_id: string) => {
  const [loading, setLoading] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);
  const authFetch = useAuthFetch();

  const updateSection = async (
    documentTypeId: number,
    sectionId: number,
    updates: SectionUpdateData,
  ): Promise<DocumentTypeSection> => {
    try {
      setLoading(true);
      const response = await authFetch(
        `/api/workspace/meridian/admin/document_types/${documentTypeId}/sections/${sectionId}?workspace_id=${workspace_id}`,
        "update section",
        {
          method: "PUT",
          headers: {
            "Content-Type": "application/json",
          },
          body: JSON.stringify(updates),
        },
      );
      return response.section || response;
    } catch (err: any) {
      setError(err.message || "Error updating section.");
      throw err;
    } finally {
      setLoading(false);
    }
  };

  return { updateSection, loading, error };
};

const useDeleteDocumentTypeSection = (workspace_id: string) => {
  const [loading, setLoading] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);
  const authFetch = useAuthFetch();

  const deleteSection = async (documentTypeId: number, sectionId: number) => {
    try {
      setLoading(true);
      await authFetch(
        `/api/workspace/meridian/admin/document_types/${documentTypeId}/sections/${sectionId}?workspace_id=${workspace_id}`,
        "delete section",
        {
          method: "DELETE",
        },
      );
    } catch (err: any) {
      setError(err.message || "Error deleting section.");
      throw err;
    } finally {
      setLoading(false);
    }
  };

  return { deleteSection, loading, error };
};

const useDocumentTypeSections = (workspace_id: string) => {
  const [loading, setLoading] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);
  const authFetch = useAuthFetch();

  const getSections = useCallback(
    async (documentTypeId: number): Promise<DocumentTypeSection[]> => {
      try {
        setLoading(true);
        const response = await authFetch(
          `/api/workspace/meridian/admin/document_types/${documentTypeId}/sections?workspace_id=${workspace_id}`,
          "fetch sections",
        );
        return response;
      } catch (err: any) {
        setError(err.message || "Failed to fetch sections");
        throw err;
      } finally {
        setLoading(false);
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps -- authFetch is stable
    [workspace_id],
  );

  return { getSections, loading, error };
};

export {
  useCreateDocumentTypeSection,
  useUpdateDocumentTypeSection,
  useDeleteDocumentTypeSection,
  useDocumentTypeSections,
};
